import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { Plus, Edit2, Trash2, Power, Search, Zap, Play, CheckCircle, XCircle, Settings, ChevronDown, ChevronUp, Save, Copy, Globe, ShieldCheck, Loader2, X, AlertTriangle, Clock, SkipForward } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ACTIONS = [
  { id: 'create_account', label: 'Create Account' },
  { id: 'recharge', label: 'Load/Recharge' },
  { id: 'redeem', label: 'Redeem' },
  { id: 'get_balance', label: 'Get Balance' },
  { id: 'get_user_id', label: 'Get User ID' },
  { id: 'get_game_balance', label: 'Game Balance' },
  { id: 'get_game_records', label: 'Records' },
  { id: 'change_password', label: 'Change Pass' },
  { id: 'give_bonus', label: 'Give Bonus' },
];

export default function ProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [testFields, setTestFields] = useState({});
  const [testingAction, setTestingAction] = useState({});
  const [rulesEditor, setRulesEditor] = useState(null);
  const [rulesData, setRulesData] = useState({});
  const [savingRules, setSavingRules] = useState(false);
  const [formData, setFormData] = useState({ code: '', name: '', api_base_url: '', api_key: '', api_secret: '', enabled: true });

  // Provider Test-All state
  const [testRunning, setTestRunning] = useState(null);    // provider_code currently being tested
  const [testReport, setTestReport] = useState(null);      // response JSON for the modal

  const runAllApiTests = async (providerCode, providerName) => {
    setTestRunning(providerCode);
    try {
      const res = await api.post(`/api/admin/game-providers/${providerCode}/test-all`);
      setTestReport({ ...res.data, provider_name: providerName });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Test run failed';
      toast.error(msg);
    } finally {
      setTestRunning(null);
    }
  };

  useEffect(() => { fetchProviders(); }, []);

  const fetchProviders = async () => {
    try { const res = await api.get('/api/admin/providers'); setProviders(res.data.data?.items || res.data.items || []); }
    catch { toast.error('Failed to load providers'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: formData.name, api_url: formData.api_base_url, api_key: formData.api_key, api_secret: formData.api_secret, enabled: formData.enabled };
      if (editingProvider) { await api.patch(`/api/admin/providers/${editingProvider.id}`, payload); toast.success('Updated'); }
      else { await api.post('/api/admin/providers', { code: formData.code, ...payload }); toast.success('Created'); }
      setShowForm(false); setEditingProvider(null); fetchProviders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (p) => { setEditingProvider(p); setFormData({ code: p.code, name: p.name, api_base_url: p.api_url || '', api_key: p.api_key || '', api_secret: p.api_secret || '', enabled: p.enabled !== false }); setShowForm(true); };
  const toggleProvider = async (p) => { try { await api.patch(`/api/admin/providers/${p.id}`, { enabled: !p.enabled }); fetchProviders(); } catch {} };
  const deleteProvider = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/api/admin/providers/${id}`); fetchProviders(); } catch {} };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  const runTest = async (providerId, action) => {
    setTestingAction(prev => ({ ...prev, [`${providerId}_${action}`]: true }));
    try {
      const fields = testFields[providerId] || {};
      const body = { action };
      if (fields.username_hint) { body.username_hint = fields.username_hint; body.username = fields.username_hint; }
      if (fields.user_id) body.user_id = fields.user_id;
      if (fields.amount) body.amount = fields.amount;
      if (fields.new_password) body.new_password = fields.new_password;
      const res = await api.post(`/api/admin/providers/${providerId}/test-api`, body);
      setTestResults(prev => ({ ...prev, [`${providerId}_${action}`]: res.data }));
      toast[res.data.ok ? 'success' : 'error'](`${action}: ${res.data.ok ? 'passed' : 'failed'}`);
    } catch (err) {
      setTestResults(prev => ({ ...prev, [`${providerId}_${action}`]: { ok: false, error: err.response?.data?.detail || err.message } }));
    } finally { setTestingAction(prev => ({ ...prev, [`${providerId}_${action}`]: false })); }
  };

  const openRules = (p) => {
    setRulesEditor(p.id);
    const defaults = { min_load: 10, max_load: 5000, load_fee_percent: 0, max_game_balance: 5000, min_redeem: 10, max_redeem: 10000, redeem_fee_percent: 5, redeem_min_multiplier: 3, redeem_max_multiplier: 10, first_time_bonus_percent: 0, regular_bonus_percent: 0, signup_bonus: 0, referral_bonus: 0, daily_load_limit: 50000, daily_redeem_limit: 50000, weekly_load_limit: 200000, weekly_redeem_limit: 200000, max_accounts_per_player: 3 };
    setRulesData({ ...defaults, ...(p.rules || {}) });
  };

  const saveRules = async () => {
    setSavingRules(true);
    try { await api.patch(`/api/admin/providers/${rulesEditor}/rules`, rulesData); toast.success('Rules saved!'); setRulesEditor(null); fetchProviders(); }
    catch { toast.error('Failed'); }
    finally { setSavingRules(false); }
  };

  const filtered = providers.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.code?.includes(search.toLowerCase()));
  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="providers-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">Game Provider Configuration</h2>
          <p className="text-sm text-gray-400">{providers.length} providers · one master automation URL powers them all.</p>
        </div>
        <Button onClick={() => { setEditingProvider(null); setFormData({ code: '', name: '', api_base_url: '', api_key: '', api_secret: '', enabled: true }); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-provider-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Provider
        </Button>
      </div>

      {/* Master Callback URL banner — single URL for every game provider. The
          automation API posts back here and includes `provider_code` in the
          JSON body so we can route it to the matching provider client. */}
      <div
        className="rounded-xl p-4 border"
        style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(108,92,231,0.06))', borderColor: 'rgba(212,175,55,0.35)' }}
        data-testid="master-callback-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-white">Master Callback URL</p>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] font-semibold uppercase">one for all providers</span>
            </div>
            <p className="text-[11px] text-gray-400 mb-2">
              Configure this URL on your automation server. Every callback (Juwa, Fire Kirin, Game Vault, Milky Way, &hellip;) posts here with <code className="px-1 rounded bg-white/5 text-[#D4AF37] font-mono">provider_code</code> in the body.
            </p>
            <div className="flex items-center gap-2 rounded-lg bg-[#0B0E14] border border-white/10 px-3 py-2">
              <code className="text-xs font-mono text-green-400 flex-1 truncate break-all" data-testid="master-callback-url">{BACKEND_URL}/api/webhooks/provider</code>
              <button
                onClick={() => copyText(`${BACKEND_URL}/api/webhooks/provider`)}
                className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-[11px] flex items-center gap-1 flex-shrink-0"
                data-testid="copy-master-callback"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search providers..." className="pl-10 bg-[#131822] border-white/10" /></div>

      <div className="space-y-3">
        {filtered.map(p => (
          <div key={p.id} className="rounded-xl border border-white/10 bg-[#131822]/80 overflow-hidden" data-testid={`provider-card-${p.code}`}>
            {/* Header row */}
            <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-white/[0.02] transition" onClick={() => setExpandedProvider(expandedProvider === p.id ? null : p.id)}>
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-lg flex-shrink-0">{p.name?.charAt(0)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold">{p.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{p.enabled ? 'ACTIVE' : 'DISABLED'}</span>
                </div>
                <p className="text-xs text-gray-500">{p.code} {p.api_url && <span className="text-green-400 ml-1">API Connected</span>}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={e => { e.stopPropagation(); runAllApiTests(p.code, p.name); }}
                  disabled={testRunning === p.code}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-500/20 border border-emerald-500/25 transition disabled:opacity-60"
                  data-testid={`test-all-btn-${p.code}`}
                  title="Run every automation API against this provider"
                >
                  {testRunning === p.code
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Testing…</>
                    : <><ShieldCheck className="w-3 h-3" /> Test APIs</>}
                </button>
                <button onClick={e => { e.stopPropagation(); openRules(p); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-[11px] text-gray-400 hover:bg-white/10 border border-white/5 transition"><Settings className="w-3 h-3" /> Rules</button>
                <button onClick={e => { e.stopPropagation(); openEdit(p); }} className="p-1.5 hover:bg-white/5 rounded-lg transition"><Edit2 className="w-3.5 h-3.5 text-gray-400" /></button>
                <button onClick={e => { e.stopPropagation(); toggleProvider(p); }} className={`p-1.5 hover:bg-white/5 rounded-lg transition ${p.enabled ? 'text-green-400' : 'text-gray-600'}`}><Power className="w-3.5 h-3.5" /></button>
                <button onClick={e => { e.stopPropagation(); deleteProvider(p.id); }} className="p-1.5 hover:bg-white/5 rounded-lg transition text-red-400/60 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                {expandedProvider === p.id ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </div>
            </div>

            {/* Expanded details */}
            {expandedProvider === p.id && (
              <div className="border-t border-white/5 p-4 space-y-4 bg-[#0B0E14]/50">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Info label="API Base URL" value={p.api_url || 'Using master config'} ok />
                  <Info label="API Key" value={p.api_key ? `${p.api_key.slice(0, 14)}...` : 'Using master config'} ok />
                  <Info label="API Secret" value={p.api_secret ? 'Configured' : 'Using master config'} ok />
                </div>
                <div className="rounded-lg p-2.5 bg-[#D4AF37]/5 border border-[#D4AF37]/15 flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                  <p className="text-[11px] text-gray-400">
                    Callback URL for this provider → see the <span className="text-[#D4AF37] font-semibold">Master Callback URL</span> banner at the top. Automation posts <code className="text-[#D4AF37] font-mono">{`{ "provider_code": "${p.code}" }`}</code> there.
                  </p>
                </div>
                {p.rules && (
                  <div className="bg-[#131822] rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 font-medium mb-2">ACTIVE RULES</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                      <span><span className="text-gray-500">Load:</span> <span className="text-white">{p.rules.min_load}-{p.rules.max_load}</span></span>
                      <span><span className="text-gray-500">Redeem:</span> <span className="text-white">{p.rules.min_redeem}-{p.rules.max_redeem}</span></span>
                      <span><span className="text-gray-500">1st Bonus:</span> <span className="text-[#D4AF37]">{p.rules.first_time_bonus_percent}%</span></span>
                      <span><span className="text-gray-500">Fee:</span> <span className="text-white">{p.rules.redeem_fee_percent}%</span></span>
                    </div>
                  </div>
                )}
                {/* API Test Panel */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-[#D4AF37]" /> API Testing</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                    <Input placeholder="username" value={testFields[p.id]?.username_hint || ''} onChange={e => setTestFields(pr => ({ ...pr, [p.id]: { ...pr[p.id], username_hint: e.target.value } }))} className="bg-[#131822] border-white/10 text-xs h-8" />
                    <Input placeholder="user_id" value={testFields[p.id]?.user_id || ''} onChange={e => setTestFields(pr => ({ ...pr, [p.id]: { ...pr[p.id], user_id: e.target.value } }))} className="bg-[#131822] border-white/10 text-xs h-8" />
                    <Input placeholder="amount" value={testFields[p.id]?.amount || ''} onChange={e => setTestFields(pr => ({ ...pr, [p.id]: { ...pr[p.id], amount: e.target.value } }))} className="bg-[#131822] border-white/10 text-xs h-8" />
                    <Input placeholder="new_password" value={testFields[p.id]?.new_password || ''} onChange={e => setTestFields(pr => ({ ...pr, [p.id]: { ...pr[p.id], new_password: e.target.value } }))} className="bg-[#131822] border-white/10 text-xs h-8" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ACTIONS.map(a => {
                      const k = `${p.id}_${a.id}`, r = testResults[k], t = testingAction[k];
                      return (
                        <button key={a.id} onClick={() => runTest(p.id, a.id)} disabled={t} data-testid={`test-${p.code}-${a.id}`}
                          className={`px-2 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 border transition ${r?.ok ? 'bg-green-500/15 text-green-400 border-green-500/20' : r && !r.ok ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-white/[0.03] text-gray-400 border-white/10 hover:bg-white/[0.06]'}`}>
                          {t ? <div className="spinner w-3 h-3" /> : r?.ok ? <CheckCircle className="w-3 h-3" /> : r && !r.ok ? <XCircle className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          {a.label}
                        </button>
                      );
                    })}
                  </div>
                  {Object.entries(testResults).filter(([k]) => k.startsWith(p.id)).length > 0 && (
                    <div className="mt-2 bg-[#131822] rounded-lg p-2 max-h-40 overflow-y-auto">
                      <pre className="text-[10px] text-gray-400 whitespace-pre-wrap">{JSON.stringify(Object.fromEntries(Object.entries(testResults).filter(([k]) => k.startsWith(p.id)).map(([k,v]) => [k.split('_').slice(1).join('_'), v.ok ? '✓' : v.error || 'failed'])), null, 2)}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Provider Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editingProvider ? 'Edit Provider' : 'Add Provider'} maxWidth="max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-gray-400 mb-1 block">Code</label><Input value={formData.code} onChange={e => setFormData(p => ({ ...p, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="juwa" className="bg-[#0B0E14] border-white/10 font-mono" required disabled={!!editingProvider} data-testid="provider-code" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">Name</label><Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Juwa" className="bg-[#0B0E14] border-white/10" required data-testid="provider-name" /></div>
          </div>
          <div><label className="text-xs text-gray-400 mb-1 block">API Base URL</label><Input value={formData.api_base_url} onChange={e => setFormData(p => ({ ...p, api_base_url: e.target.value }))} placeholder="https://..." className="bg-[#0B0E14] border-white/10" /></div>
          <div><label className="text-xs text-gray-400 mb-1 block">API Key</label><Input value={formData.api_key} onChange={e => setFormData(p => ({ ...p, api_key: e.target.value }))} placeholder="gk_..." className="bg-[#0B0E14] border-white/10 font-mono" /></div>
          <div><label className="text-xs text-gray-400 mb-1 block">API Secret</label><Input value={formData.api_secret} onChange={e => setFormData(p => ({ ...p, api_secret: e.target.value }))} placeholder="ws_..." className="bg-[#0B0E14] border-white/10 font-mono" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.enabled} onChange={e => setFormData(p => ({ ...p, enabled: e.target.checked }))} className="w-4 h-4 accent-[#D4AF37]" /> Enabled</label>
          <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
            <Button type="submit" className="bg-[#D4AF37] text-black" data-testid="provider-submit">{editingProvider ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* Rules Modal */}
      <Modal open={!!rulesEditor} onClose={() => setRulesEditor(null)} title={`Rules - ${providers.find(p => p.id === rulesEditor)?.name || ''}`} maxWidth="max-w-xl">
        <div className="space-y-5">
          <Section title="LOAD / RECHARGE RULES">
            <div className="grid grid-cols-2 gap-3">
              <R label="Min Load ($)" v={rulesData.min_load} set={v => setRulesData(p => ({ ...p, min_load: v }))} />
              <R label="Max Load ($)" v={rulesData.max_load} set={v => setRulesData(p => ({ ...p, max_load: v }))} />
              <R label="Load Fee %" v={rulesData.load_fee_percent} set={v => setRulesData(p => ({ ...p, load_fee_percent: v }))} />
              <R label="Max Game Balance" v={rulesData.max_game_balance} set={v => setRulesData(p => ({ ...p, max_game_balance: v }))} />
              <R label="Daily Load Limit" v={rulesData.daily_load_limit} set={v => setRulesData(p => ({ ...p, daily_load_limit: v }))} />
              <R label="Weekly Load Limit" v={rulesData.weekly_load_limit} set={v => setRulesData(p => ({ ...p, weekly_load_limit: v }))} />
            </div>
          </Section>
          <Section title="REDEEM RULES">
            <div className="grid grid-cols-2 gap-3">
              <R label="Min Redeem ($)" v={rulesData.min_redeem} set={v => setRulesData(p => ({ ...p, min_redeem: v }))} />
              <R label="Max Redeem ($)" v={rulesData.max_redeem} set={v => setRulesData(p => ({ ...p, max_redeem: v }))} />
              <R label="Redeem Fee %" v={rulesData.redeem_fee_percent} set={v => setRulesData(p => ({ ...p, redeem_fee_percent: v }))} />
              <R label="Min Play Multiplier (x)" v={rulesData.redeem_min_multiplier} set={v => setRulesData(p => ({ ...p, redeem_min_multiplier: v }))} />
              <R label="Max Play Multiplier (x)" v={rulesData.redeem_max_multiplier} set={v => setRulesData(p => ({ ...p, redeem_max_multiplier: v }))} />
              <R label="Daily Redeem Limit" v={rulesData.daily_redeem_limit} set={v => setRulesData(p => ({ ...p, daily_redeem_limit: v }))} />
            </div>
          </Section>
          <Section title="BONUS RULES">
            <div className="grid grid-cols-2 gap-3">
              <R label="First-Time Load Bonus % (FP only)" v={rulesData.first_time_bonus_percent} set={v => setRulesData(p => ({ ...p, first_time_bonus_percent: v }))} />
              <R label="Regular Load Bonus % (FP only)" v={rulesData.regular_bonus_percent} set={v => setRulesData(p => ({ ...p, regular_bonus_percent: v }))} />
              <R label="Signup Bonus (FP)" v={rulesData.signup_bonus} set={v => setRulesData(p => ({ ...p, signup_bonus: v }))} />
              <R label="Referral Bonus (FP)" v={rulesData.referral_bonus} set={v => setRulesData(p => ({ ...p, referral_bonus: v }))} />
            </div>
          </Section>
          <Section title="ACCOUNT RULES">
            <R label="Max Accounts / Player" v={rulesData.max_accounts_per_player} set={v => setRulesData(p => ({ ...p, max_accounts_per_player: v }))} />
          </Section>
          <Button onClick={saveRules} disabled={savingRules} className="w-full bg-[#D4AF37] text-black font-semibold" data-testid="save-provider-rules">
            <Save className="w-4 h-4 mr-2" /> {savingRules ? 'Saving...' : 'Save Rules'}
          </Button>
        </div>
      </Modal>

      {/* Test-All Results Modal */}
      {testReport && (
        <TestReportModal report={testReport} onClose={() => setTestReport(null)} />
      )}
    </div>
  );
}

function Info({ label, value, ok }) {
  return (
    <div className="bg-[#131822] rounded-lg p-2.5">
      <p className="text-[10px] text-gray-500 mb-0.5">{label}</p>
      <p className={`text-xs font-mono truncate ${ok ? 'text-green-400' : 'text-red-400/70'}`}>{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs text-[#D4AF37] font-semibold tracking-wide">{title}</h4>
      {children}
    </div>
  );
}

function R({ label, v, set }) {
  return (
    <div>
      <label className="text-[10px] text-gray-500 mb-0.5 block">{label}</label>
      <Input type="number" value={v === 0 || v ? v : ''} onChange={e => { const val = e.target.value; set(val === '' ? 0 : parseFloat(val)); }} className="bg-[#0B0E14] border-white/10 text-xs h-9" />
    </div>
  );
}

/**
 * Shows a per-test breakdown of the `test-all` run with pass/fail icons,
 * duration, messages, and a raw-response drawer for debugging.
 */
function TestReportModal({ report, onClose }) {
  const totalPassed = report.tests.filter(t => t.ok).length;
  const totalSkipped = report.tests.filter(t => t.status === 'skipped' || t.status === 'not_supported').length;
  const totalFailed = report.tests.filter(t => !t.ok && t.status !== 'skipped' && t.status !== 'not_supported').length;
  const [openTest, setOpenTest] = useState(null);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="test-report-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-[#0F1320] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-5 border-b border-white/5"
          style={{ background: report.overall_ok
            ? 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(212,175,55,0.05))'
            : 'linear-gradient(135deg, rgba(239,68,68,0.10), rgba(212,175,55,0.05))' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  report.overall_ok ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}
              >
                {report.overall_ok
                  ? <CheckCircle className="w-6 h-6 text-green-400" />
                  : <AlertTriangle className="w-6 h-6 text-red-400" />}
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-white truncate">
                  {report.provider_name || report.provider_code} API Test Results
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  <span className="text-green-400 font-semibold">{totalPassed} passed</span>
                  {totalFailed > 0 && <> · <span className="text-red-400 font-semibold">{totalFailed} failed</span></>}
                  {totalSkipped > 0 && <> · <span className="text-gray-400 font-semibold">{totalSkipped} not supported</span></>}
                  {' '}· via <span className="text-[#D4AF37]">{report.config_source || 'n/a'}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition flex-shrink-0"
              data-testid="close-test-report"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {report.base_url && (
            <div className="mt-3 flex items-center gap-2 text-[11px]">
              <Globe className="w-3 h-3 text-gray-500" />
              <code className="font-mono text-gray-400 truncate flex-1">{report.base_url}</code>
            </div>
          )}
        </div>

        {/* Per-test rows */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {report.tests.map((t, idx) => {
            const isOpen = openTest === idx;
            const isSkip = t.status === 'skipped' || t.status === 'not_supported';
            const icon = isSkip
              ? <SkipForward className="w-4 h-4 text-gray-500" />
              : t.ok
                ? <CheckCircle className="w-4 h-4 text-green-400" />
                : <XCircle className="w-4 h-4 text-red-400" />;
            const rowBg = isSkip
              ? 'bg-white/[0.02]'
              : t.ok ? 'bg-green-500/5' : 'bg-red-500/5';
            const rowBorder = isSkip
              ? 'border-white/5'
              : t.ok ? 'border-green-500/15' : 'border-red-500/20';
            return (
              <div
                key={t.name}
                className={`rounded-xl border ${rowBorder} ${rowBg} overflow-hidden`}
                data-testid={`test-row-${t.name}`}
              >
                <button
                  onClick={() => setOpenTest(isOpen ? null : idx)}
                  className="w-full p-3 flex items-center gap-3 text-left hover:bg-white/[0.02] transition"
                >
                  {icon}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{t.label}</p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {t.message || (t.ok ? 'Completed successfully' : 'No message')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t.duration_ms}ms
                    </span>
                    {t.data && (isOpen
                      ? <ChevronUp className="w-4 h-4 text-gray-500" />
                      : <ChevronDown className="w-4 h-4 text-gray-500" />)}
                  </div>
                </button>
                {isOpen && t.data && (
                  <div className="px-3 pb-3">
                    <pre className="text-[10px] text-gray-300 bg-black/40 rounded-lg p-2 overflow-x-auto max-h-52 overflow-y-auto border border-white/5">
{JSON.stringify(t.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 flex items-center justify-between gap-3 bg-[#0B0E14]">
          <p className="text-[10px] text-gray-500">
            Started {new Date(report.started_at).toLocaleTimeString()} · Finished {new Date(report.finished_at).toLocaleTimeString()}
          </p>
          <Button onClick={onClose} className="bg-white/5 text-white border border-white/10 hover:bg-white/10">Close</Button>
        </div>
      </div>
    </div>
  );
}
